import { jsPDF } from "jspdf";
import type { PermitFormField, TableColumn } from "./formUtils";
import { getFieldKey, isFieldHidden } from "./formUtils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GeneratePDFOptions {
  formData: {
    title?: string | null;
    section?: Array<{
      _key?: string | null;
      title?: any;
      formFields?: Array<PermitFormField | null> | null;
    } | null> | null;
  };
  formInputs: Record<string, unknown>;
  /** ISO date string for the "Submitted:" line. Defaults to now (live-site behaviour). */
  submittedAt?: string | null;
}

// ---------------------------------------------------------------------------
// Text sanitisation
// ---------------------------------------------------------------------------

/**
 * Sanitize text for jsPDF's built-in Helvetica font, which only covers Latin-1
 * (U+0000–U+00FF). Characters outside that range render as wide invisible glyphs,
 * causing words to appear spaced apart.
 *
 * Replaces common Unicode punctuation with ASCII equivalents, strips combining
 * macrons, then drops any remaining non-Latin-1 characters.
 */
function stripMacrons(text: string): string {
  return (
    text
      // Smart / curly quotes → straight quotes
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
      // Dashes: non-breaking hyphen, figure dash, en-dash → hyphen; em-dash → --
      .replace(/[\u2011\u2012\u2013]/g, "-")
      .replace(/[\u2014\u2015]/g, "--")
      // Horizontal ellipsis → three dots
      .replace(/\u2026/g, "...")
      // All Unicode space variants → regular space
      .replace(/[\u00A0\u202F\u2009\u200A\u2008\u2007\u2006\u2005\u2004\u2003\u2002\u2001\u3000]/g, " ")
      // Tabs → single space
      .replace(/\t/g, " ")
      // Zero-width chars → remove
      .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
      // Bullet variants → ASCII hyphen-dash list item
      .replace(/[\u2022\u2023\u2024\u2025\u2043\u204C\u204D]/g, "-")
      // Strip combining macrons (ā → a, ē → e, etc.)
      .normalize("NFD")
      .replace(/\u0304/g, "")
      // Drop anything still outside Latin-1
      .replace(/[^\x00-\xFF]/g, "")
      // Collapse runs of 2+ spaces on each line independently (preserves newlines)
      .split("\n")
      .map((line) => line.replace(/ {2,}/g, " ").trimEnd())
      .join("\n")
  );
}

// ---------------------------------------------------------------------------
// Value normalisation
// ---------------------------------------------------------------------------

/**
 * Try to parse a JSON string into a native value.
 * Handles arrays (checkbox, table rows) and objects (file metadata) from
 * CSV / Netlify API representations. Non-JSON strings are returned as-is.
 */
function tryParseJSON(val: unknown): unknown {
  if (typeof val !== "string") return val;
  const trimmed = val.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return val;
  try {
    return JSON.parse(trimmed);
  } catch {
    return val;
  }
}

/**
 * Format a file-metadata object (from CSV / Netlify API) as a readable string.
 * Shape: { filename?, name?, url?, downloadUrl?, src? }
 */
function formatFileObject(obj: Record<string, unknown>): string {
  const name = obj.filename ?? obj.name ?? obj.fileName ?? obj.originalname;
  const url  = obj.url ?? obj.downloadUrl ?? obj.src;
  const parts: string[] = [];
  if (name) parts.push(`File: ${stripMacrons(String(name))}`);
  if (url)  parts.push(String(url)); // preserve URL exactly for click-ability
  return parts.length > 0 ? parts.join("\n") : "(File upload)";
}

// ---------------------------------------------------------------------------
// Block-content text extraction (Sanity rich-text titles)
// ---------------------------------------------------------------------------

function extractBlockText(obj: unknown): string {
  if (typeof obj === "string") return obj;
  if (Array.isArray(obj)) return obj.map(extractBlockText).filter(Boolean).join(" ");
  if (obj && typeof obj === "object") {
    const o = obj as Record<string, unknown>;
    if (Array.isArray(o._rawBlockContent)) return extractBlockText(o._rawBlockContent);
    if (o.children) return extractBlockText(o.children);
    if (typeof o.text === "string") return o.text;
    if (Array.isArray(o.blockContent)) return extractBlockText(o.blockContent);
    if (o._type === "block" && o.children) return extractBlockText(o.children);
    if (o._type === "heading" && typeof o.text === "string") return o.text;
  }
  return "";
}

// ---------------------------------------------------------------------------
// Core PDF renderer — no browser APIs, works in Node.js too
// ---------------------------------------------------------------------------

/**
 * Generates the PDF and returns its bytes as an ArrayBuffer.
 * Safe to call in Node.js (no File / URL.createObjectURL).
 */
export function generatePDFBuffer({ formData, formInputs, submittedAt }: GeneratePDFOptions): ArrayBuffer {
  const doc = new jsPDF();
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin   = 8;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition  = margin + 5;

  const colors = {
    heading: [44, 62, 80]  as [number, number, number],
    section: [52, 73, 94]  as [number, number, number],
    accent:  [52, 152, 219] as [number, number, number],
    label:   [44, 62, 80]  as [number, number, number],
    value:   [85, 85, 85]  as [number, number, number],
    date:    [100, 100, 100] as [number, number, number],
    rowBg:   [249, 249, 249] as [number, number, number],
    border:  [221, 221, 221] as [number, number, number],
  };

  // Form title
  if (formData.title) {
    doc.setFontSize(15);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.heading);
    const titleLines = doc.splitTextToSize(stripMacrons(formData.title), maxWidth);
    doc.text(titleLines, margin, yPosition);
    yPosition += titleLines.length * 6 + 4;
    doc.setDrawColor(...colors.accent);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
    yPosition += 6;
  }

  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin + 5;
    }
  };

  const renderLinesWithPageBreaks = (lines: string[], x: number, lineHeight: number) => {
    for (const line of lines) {
      checkPageBreak(lineHeight);
      doc.text(line, x, yPosition);
      yPosition += lineHeight;
    }
  };

  // Submission date
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.date);
  const dateText = submittedAt
    ? `Submitted: ${new Date(submittedAt).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" })}`
    : `Submitted: ${new Date().toLocaleString()}`;
  doc.text(dateText, margin, yPosition);
  yPosition += 8;

  const sections = (formData.section ?? []).filter(Boolean);

  sections.forEach((section, sectionIndex) => {
    if (!section) return;

    checkPageBreak(14);

    // Section title
    if (section.title) {
      let sectionTitle = "";
      if (typeof section.title === "string") {
        sectionTitle = section.title;
      } else if (section.title && typeof section.title === "object") {
        sectionTitle = extractBlockText(section.title) || `Section ${sectionIndex + 1}`;
      }
      if (sectionTitle) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colors.section);
        const titleLines = doc.splitTextToSize(stripMacrons(sectionTitle), maxWidth);
        doc.text(titleLines, margin, yPosition);
        yPosition += titleLines.length * 5 + 6;
      }
    }

    const fields = (section.formFields ?? []).filter(Boolean) as PermitFormField[];
    const visibleFields = fields.filter(field => field && !isFieldHidden(field, formInputs));

    let currentRow: Array<{ label: string; value: string; fullWidth: boolean; type?: string }> = [];
    let isFullWidthRow = false;
    let rowIndex = 0;

    // renderTableRow is a closure so it can mutate yPosition
    function renderTableRow(
      rowFields: Array<{ label: string; value: string; fullWidth: boolean; type?: string }>,
      isFullWidth: boolean,
    ) {
      if (rowFields.length === 0) return;

      const labelLineHeight  = 4;
      const valueLineHeight  = 4;
      const labelToValueGap  = 6;

      checkPageBreak(16);

      if (isFullWidth || rowFields.length === 1) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colors.label);
        const labelLines = doc.splitTextToSize(rowFields[0].label, maxWidth - 2);
        renderLinesWithPageBreaks(labelLines, margin + 1, labelLineHeight);
        yPosition += labelToValueGap;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...colors.value);
        const valueLines = doc.splitTextToSize(rowFields[0].value, maxWidth - 2);
        renderLinesWithPageBreaks(valueLines, margin + 1, valueLineHeight);
        yPosition += 1;
      } else {
        const cellWidth = (pageWidth - 2 * margin) / 2;
        const leftX  = margin + 1;
        const rightX = margin + cellWidth + 1;

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colors.label);
        const leftLabelLines  = doc.splitTextToSize(rowFields[0].label, cellWidth - 2);
        const rightLabelLines = doc.splitTextToSize(rowFields[1].label, cellWidth - 2);

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...colors.value);
        const leftValueLines  = doc.splitTextToSize(rowFields[0].value, cellWidth - 2);
        const rightValueLines = doc.splitTextToSize(rowFields[1].value, cellWidth - 2);

        const leftRows: { text: string; isLabel: boolean }[] = [
          ...leftLabelLines.map((t: string) => ({ text: t, isLabel: true })),
          ...leftValueLines.map((t: string) => ({ text: t, isLabel: false })),
        ];
        const rightRows: { text: string; isLabel: boolean }[] = [
          ...rightLabelLines.map((t: string) => ({ text: t, isLabel: true })),
          ...rightValueLines.map((t: string) => ({ text: t, isLabel: false })),
        ];
        const rowCount = Math.max(leftRows.length, rightRows.length);
        while (leftRows.length < rowCount)  leftRows.push({ text: "", isLabel: false });
        while (rightRows.length < rowCount) rightRows.push({ text: "", isLabel: false });

        for (let i = 0; i < rowCount; i++) {
          const left  = leftRows[i];
          const right = rightRows[i];
          const isLabelRow  = left.isLabel || right.isLabel;
          const prevWasLabel = i > 0 && (leftRows[i - 1].isLabel || rightRows[i - 1].isLabel);
          if (prevWasLabel && !isLabelRow) yPosition += labelToValueGap;
          const lineHeight = isLabelRow ? labelLineHeight : valueLineHeight;
          checkPageBreak(lineHeight);
          if (left.isLabel) {
            doc.setFontSize(10); doc.setFont("helvetica", "bold");
            doc.setTextColor(...colors.label);
          } else {
            doc.setFontSize(9); doc.setFont("helvetica", "normal");
            doc.setTextColor(...colors.value);
          }
          if (left.text)  doc.text(left.text, leftX, yPosition);
          if (right.isLabel) {
            doc.setFontSize(10); doc.setFont("helvetica", "bold");
            doc.setTextColor(...colors.label);
          } else {
            doc.setFontSize(9); doc.setFont("helvetica", "normal");
            doc.setTextColor(...colors.value);
          }
          if (right.text) doc.text(right.text, rightX, yPosition);
          yPosition += lineHeight;
        }
        yPosition += 3;
      }

      yPosition += 1;
    }

    visibleFields.forEach((field, fieldIndex) => {
      if (!field) return;

      const fieldKey = getFieldKey(field);
      const label    = stripMacrons(field.label || fieldKey);
      const fullWidth = field.fullWidth || false;

      // Normalise the raw value: parse JSON strings from CSV / Netlify API
      const value = tryParseJSON(formInputs[fieldKey]);

      // ---- Table fields ----
      if (field.type === "table") {
        const tableRows = (
          Array.isArray(value) ? value : []
        ).filter(
          (row): row is Record<string, unknown> => typeof row === "object" && row !== null && !(row instanceof File),
        );

        if (tableRows.length > 0) {
          const columns = (field.tableColumns || []).filter((col): col is TableColumn => col != null);

          checkPageBreak(30);

          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...colors.label);
          doc.text(label, margin, yPosition);
          yPosition += 7;

          const tableWidth  = pageWidth - 2 * margin;
          const columnCount = columns.length || 1;
          const columnWidth = tableWidth / columnCount;
          const rowHeight   = 12;

          checkPageBreak(rowHeight * (tableRows.length + 1) + 10);

          // Header row
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setFillColor(...colors.accent);
          doc.rect(margin, yPosition - 5, tableWidth, rowHeight, "F");
          doc.setTextColor(255, 255, 255);

          let xPos = margin + 5;
          columns.forEach(col => {
            const colLabel = stripMacrons(col.label || col.id || "");
            doc.text(doc.splitTextToSize(colLabel, columnWidth - 10), xPos, yPosition);
            xPos += columnWidth;
          });
          yPosition += rowHeight + 1;

          // Data rows
          doc.setTextColor(...colors.value);
          tableRows.forEach((row, rIdx) => {
            checkPageBreak(rowHeight + 3);
            if (rIdx % 2 === 1) {
              doc.setFillColor(...colors.rowBg);
              doc.rect(margin, yPosition - 5, tableWidth, rowHeight, "F");
            }
            doc.setFont("helvetica", "normal");
            xPos = margin + 5;
            columns.forEach(col => {
              const cellValue = row[col.id || ""];
              let cellText = cellValue != null ? stripMacrons(String(cellValue)) : "";
              if (col.type === "amount" && cellText) {
                const normalized = cellText.replace(/,/g, "").replace(/^NZ\$/i, "").trim();
                const num = parseFloat(normalized);
                cellText = !isNaN(num)
                  ? `NZ$${num.toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : `NZ$${normalized}`;
              }
              doc.text(doc.splitTextToSize(cellText, columnWidth - 10), xPos, yPosition);
              xPos += columnWidth;
            });
            yPosition += rowHeight + 1;
          });
          yPosition += 3;
        }
        return; // skip normal field rendering
      }

      // ---- Regular fields ----
      let displayValue = "";
      const isCheckboxField = field.type === "checkbox";
      const isFileField     = field.type === "file";

      if (value instanceof File) {
        displayValue = `File: ${stripMacrons(value.name)}`;
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        !(value instanceof File)
      ) {
        // File metadata object from CSV / Netlify API: { filename, url, ... }
        displayValue = formatFileObject(value as Record<string, unknown>);
      } else if (Array.isArray(value)) {
        const hasFiles = value.some(v => v instanceof File);
        if (hasFiles && isFileField) {
          const fileNames = value
            .filter((v): v is File => v instanceof File)
            .map(file => file.name);
          displayValue = fileNames.length > 0
            ? "- " + fileNames.map(n => stripMacrons(n)).join("\n- ")
            : "";
        } else if (value.length > 0 && typeof value[0] === "object" && !(value[0] instanceof File)) {
          // Array of objects that aren't table rows (shouldn't normally reach here)
          return;
        } else if (isCheckboxField && value.length > 0) {
          const checkboxItems = (value as unknown[])
            .map(v => stripMacrons(String(v)).trim())
            .filter(Boolean);
          displayValue = checkboxItems.length > 0 ? "- " + checkboxItems.join("\n- ") : "";
        } else if (isFileField && value.length > 0) {
          const fileNames = (value as unknown[])
            .map(v => {
              if (v instanceof File) return v.name;
              if (typeof v === "string" && v !== "[object File]") return v;
              return null;
            })
            .filter((name): name is string => name !== null);
          displayValue = fileNames.length > 0
            ? "- " + fileNames.map(n => stripMacrons(n)).join("\n- ")
            : "";
        } else {
          displayValue = (value as unknown[]).length > 0
            ? (value as unknown[]).map(v => stripMacrons(String(v))).join(", ")
            : "";
        }
      } else if (typeof value === "boolean") {
        displayValue = value ? "Yes" : "No";
      } else if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true")       displayValue = "Yes";
        else if (normalized === "false") displayValue = "No";
        else if (isCheckboxField && value.includes("\n")) {
          // Newline-separated checkbox values (older Netlify format)
          const items = value.split("\n").map(l => stripMacrons(l.trim())).filter(Boolean);
          displayValue = items.length > 0 ? "- " + items.join("\n- ") : stripMacrons(value);
        } else {
          displayValue = stripMacrons(value);
        }
      } else if (value !== undefined && value !== null) {
        displayValue = stripMacrons(String(value));
      }

      if (field.type === "amount" && displayValue) {
        const normalized = displayValue.replace(/,/g, "").replace(/^NZ\$/i, "").trim();
        if (normalized) {
          const num = parseFloat(normalized);
          displayValue = !isNaN(num)
            ? `NZ$${num.toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : `NZ$${normalized}`;
        } else {
          displayValue = "";
        }
      }

      const entry = {
        label,
        value: displayValue || "(Not provided)",
        fullWidth,
        type: field.type || undefined,
      };

      if (currentRow.length === 0) {
        isFullWidthRow = fullWidth;
        currentRow = [entry];
      } else if (fullWidth || isFullWidthRow) {
        renderTableRow(currentRow, isFullWidthRow);
        currentRow = [entry];
        isFullWidthRow = fullWidth;
        rowIndex++;
      } else if (currentRow.length === 1 && !isFullWidthRow) {
        currentRow.push(entry);
      } else {
        renderTableRow(currentRow, isFullWidthRow);
        currentRow = [entry];
        isFullWidthRow = fullWidth;
        rowIndex++;
      }

      if (fieldIndex === visibleFields.length - 1) {
        renderTableRow(currentRow, isFullWidthRow);
      }
    });

    if (sectionIndex < sections.length - 1) yPosition += 6;
  });

  return doc.output("arraybuffer") as ArrayBuffer;
}

// ---------------------------------------------------------------------------
// Browser wrapper — creates File + blob URL from the ArrayBuffer
// ---------------------------------------------------------------------------

/**
 * Generates a PDF from form data and returns both the PDF File and blob URL for display.
 * Browser-only (uses URL.createObjectURL and File).
 */
export function generateFormPDF({
  formData,
  formInputs,
}: Omit<GeneratePDFOptions, "submittedAt">): { file: File; blobUrl: string } {
  const buffer = generatePDFBuffer({ formData, formInputs });
  const blob = new Blob([buffer], { type: "application/pdf" });

  const formTitleSlug = formData.title
    ? formData.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").substring(0, 50)
    : "form";
  const timestamp   = new Date().toISOString().replace(/[:.]/g, "-").substring(0, 19);
  const pdfFileName = `${formTitleSlug}-${timestamp}.pdf`;
  const pdfFile     = new File([blob], pdfFileName, { type: "application/pdf" });
  const blobUrl     = URL.createObjectURL(blob);

  return { file: pdfFile, blobUrl };
}
